/**
 * Onboarding holdout state (24h cooldown, max 3 exposures, don't show again after completion).
 * Uses AtlBrowserStorageLocal for persistence. If productionized, consider a more permanent
 * source (e.g. backend or user preferences).
 */
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

import { MS_IN_SECOND, SECONDS_IN_HR } from '../../common/constants/private';

// If productionized - will consider relocating logic to conversion-assistant-onboarding -- tracking ticket: https://hello.jira.atlassian.cloud/browse/MOOG-146

export const LS_ONBOARDING_HOLDOUT_KEY = 'rovoOnboardingHoldout';

export type OnboardingHoldoutState = {
	lastSeenAt: number;
	exposureCount: number;
	onboardingCompleted: boolean;
};

export const HOLDOUT_MS: number = 24 * SECONDS_IN_HR * MS_IN_SECOND;
export const MAX_ONBOARDING_EXPOSURES = 3;

const DEFAULT_HOLDOUT_STATE: OnboardingHoldoutState = {
	lastSeenAt: 0,
	exposureCount: 0,
	onboardingCompleted: false,
};

function parseState(raw: string | null): OnboardingHoldoutState | null {
	if (raw === null) {
		return null;
	}

	const parsed = JSON.parse(raw) as Record<string, unknown>;

	if (typeof parsed.lastSeenAt === 'number' && typeof parsed.exposureCount === 'number') {
		return {
			lastSeenAt: parsed.lastSeenAt,
			exposureCount: Math.min(
				Math.max(0, Math.floor(parsed.exposureCount)),
				MAX_ONBOARDING_EXPOSURES,
			),
			onboardingCompleted: parsed.onboardingCompleted === true,
		};
	}

	return null;
}

export function getOnboardingHoldoutState(): OnboardingHoldoutState | null {
	try {
		return parseState(AtlBrowserStorageLocal.getItem(LS_ONBOARDING_HOLDOUT_KEY));
	} catch {
		return null;
	}
}

/**
 * True if holdout rules allow showing onboarding: not within 24h cooldown,
 * under max exposures (3), and user has not completed onboarding.
 *
 * Returns false when AtlBrowserStorageLocal is unavailable or data is corrupt,
 * only returns true for first-time users (no key) or valid passing state.
 */
export function passesOnboardingHoldout(): boolean {
	try {
		const raw = AtlBrowserStorageLocal.getItem(LS_ONBOARDING_HOLDOUT_KEY);

		if (raw === null) {
			return true;
		}

		const state = parseState(raw);
		if (state === null) {
			return false;
		}

		if (state.onboardingCompleted || state.exposureCount >= MAX_ONBOARDING_EXPOSURES) {
			return false;
		}
		return Date.now() - state.lastSeenAt >= HOLDOUT_MS;
	} catch {
		return false;
	}
}

function persistState(state: OnboardingHoldoutState): void {
	try {
		AtlBrowserStorageLocal.setItem(LS_ONBOARDING_HOLDOUT_KEY, JSON.stringify(state));
	} catch {
		// AtlBrowserStorageLocal may be full or unavailable (e.g. private browsing in Safari)
	}
}

/**
 * Record one onboarding exposure (increments exposure count and sets lastSeenAt).
 * No-op if onboarding was completed or exposureCount is already 3.
 */
export function recordOnboardingExposureForHoldout(): void {
	const state = getOnboardingHoldoutState() ?? DEFAULT_HOLDOUT_STATE;

	if (state.onboardingCompleted || state.exposureCount >= MAX_ONBOARDING_EXPOSURES) {
		return;
	}

	persistState({
		...state,
		lastSeenAt: Date.now(),
		exposureCount: state.exposureCount + 1,
	});
}

/**
 * Record that the user completed the onboarding flow (e.g. selected a prompt).
 * They will never be shown onboarding again. Keeps existing exposure count;
 * completion is separate from max-exposure holdout.
 */
export function recordOnboardingCompletedForHoldout(): void {
	const state = getOnboardingHoldoutState() ?? DEFAULT_HOLDOUT_STATE;

	persistState({
		...state,
		lastSeenAt: Date.now(),
		onboardingCompleted: true,
	});
}
